import React from 'react';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';

import { TabDataCard } from '@repo/ui/Tabs';
import { DataCardGroup } from '@repo/ui/Card';
import { formatDate } from '@repo/util/formatDate';
import { InfoPopover } from '@repo/ui/Popover';

import DIDS_SHOW_Q from '../gql/show.gql';
import GET_API_KEYS_FOR_DID from '../gql/getApiKeysForDid.gql';
import { useAccount } from '@/hooks/useAccount';
import { API_KEY_ANCHOR } from './ChangeDidApiKeyForm';
import { SWITCH_ANCHOR } from './ChangeDidSwitchForm';
import { ResultOf } from '@graphql-typed-document-node/core';

export interface DidOverviewProps {
  did: ResultOf<typeof DIDS_SHOW_Q>['did'];
  loading: boolean;
}

const linkClass = 'underline';

const DidOverview = ({ did, loading }: DidOverviewProps) => {
  const accountId = useAccount().id;
  const updatePath = did?.id ? `/a/${accountId}/numbers/${did.id}/update` : null;

  const { data: apiKeysData } = useQuery(GET_API_KEYS_FOR_DID, {
    variables: { id: did?.id, first: 1 },
    skip: !did?.id,
  });
  const apiKey = apiKeysData?.apiKeysForDid?.edges[0]?.node;

  const changeLink = (anchor: string) =>
    updatePath ? (
      <Link to={`${updatePath}#${anchor}`} className="ml-2 text-sm underline">
        Update
      </Link>
    ) : null;

  const DidOverviewData: DataCardGroup[] = [
    {
      groupTitle: 'overview',
      columnCount: 3,
      groupData: [
        {
          label: 'Type',
          entry: (
            <div className="">
              {did?.type === 'TENDLC' ? <span>10DLC</span> : <span>{did?.type}</span>}
            </div>
          ),
        },

        {
          label: 'Activation Date',
          entry: loading || !did?.insertedAt ? '' : formatDate(did.insertedAt),
        },

        {
          label: 'BYON',
          entry: (
            <InfoPopover btnText={did?.isByon ? 'Yes' : 'No'} btnIcon="info" size="sm">
              <span className="break-normal text-pretty">
                {did?.isByon
                  ? 'BYON (Bring Your Own Number): A landline or VoIP number you own with your current carrier that you have connected to Swift to enable SMS messaging on your account.   '
                  : 'Number has been provided by Swift SMS Gateway'}
              </span>
            </InfoPopover>
          ),
        },
      ],
    },
    {
      groupTitle: 'connections',
      columnCount: 3,
      groupData: [
        {
          label: 'API Key',
          entry: (
            <div>
              {apiKey ? (
                <Link to={`/a/${accountId}/develop/${apiKey.id}`} className={linkClass}>
                  {apiKey.name}
                </Link>
              ) : (
                <span>None</span>
              )}
              {changeLink(API_KEY_ANCHOR)}
            </div>
          ),
        },
        {
          label: 'Switch',
          entry: (
            <div>
              {did?.switch ? (
                <Link to={`/a/${accountId}/switches/${did.switch.id}`} className={linkClass}>
                  {did.switch.name}
                </Link>
              ) : (
                <span>None</span>
              )}
              {changeLink(SWITCH_ANCHOR)}
            </div>
          ),
        },
      ],
    },
    {
      groupTitle: 'capabilities',
      columnCount: 2,
      groupData: [
        {
          label: 'Capabilities',
          entry: (
            <div className="">
              {did?.smsEnabled && <span>SMS</span>}
              {did?.smsEnabled && did?.mmsEnabled && ', '}
              {did?.mmsEnabled && <span>MMS</span>}
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <TabDataCard
      title={did?.number ?? '???'}
      loading={loading}
      skeletonCount={4}
      data={DidOverviewData}
      size="md"
    />
  );
};

export default DidOverview;
